import { Image } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import * as styles from './UserCardActionsContainer.css';
import { trackClickCallButton, trackUserProfileClick } from '../../actions/userCard/tracking';
import { TLayoutUserCardAction, UserCardActions } from '../../components/UserCard/UserCardActions';
import { UserCardDesktopPhone } from '../../components/UserCard/UserCardDesktopPhone';
import { UserCardDesktopProfileLink } from '../../components/UserCard/UserCardDesktopProfileLink';
import { useCurrentChatCompanionData } from '../../hooks';
import { useIsDailyrentOnlineBooking } from '../../hooks/useIsDailyrentOnlineBooking';
import { currentChatOpponentIdSelector, currentOpenChatSelector, customActionsSelector } from '../../selectors';
import { selectIsOpponentContactsVisible } from '../../selectors/isOpponentContactsVisible';
import { UserCardCustomActionButtonContainer } from '../UserCardCustomActionButtonContainer';

export const UserCardActionsContainer = () => {
  const currentChat = useSelector(currentOpenChatSelector);
  const customActions = useSelector(customActionsSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);
  const isOpponentContactsVisible = useSelector(selectIsOpponentContactsVisible);
  const isDailyrentOnlineBooking = useIsDailyrentOnlineBooking();

  const { companionId, formatPhone, profileLink, agentAccountType } = useCurrentChatCompanionData(currentChat?.chatId);

  const subscribeActionButton = React.useMemo(() => {
    const button = customActions
      ? customActions.find(({ action }) => action === 'unsubscribe' || action === 'subscribe')
      : undefined;

    return button || null;
  }, [customActions]);

  if (!currentChat || !companionId) {
    return null;
  }

  const { chatId } = currentChat;

  const handleClickCallButton = () => {
    trackClickCallButton(currentChat, companionId, opponentId);
  };

  const handleClickProfile = () => {
    trackUserProfileClick(currentChat, companionId, opponentId);
  };

  const isPhoneVisible = !!formatPhone && !isDailyrentOnlineBooking && isOpponentContactsVisible;

  const customActionItems: TLayoutUserCardAction[] = [];

  if (subscribeActionButton) {
    customActionItems.push({
      icon: (
        <Image
          height={16}
          borderRadius="0"
          objectFit="contain"
          src={subscribeActionButton.desktopIconUrl}
          data-testid="image"
        />
      ),
      content: <UserCardCustomActionButtonContainer button={subscribeActionButton} chatId={chatId} />,
    });
  }

  if (customActions) {
    customActions.forEach(button => {
      if (button.action !== 'unsubscribe' && button.action !== 'subscribe') {
        customActionItems.push({
          icon: (
            <Image height={16} borderRadius="0" objectFit="contain" src={button.desktopIconUrl} data-testid="image" />
          ),
          content: <UserCardCustomActionButtonContainer button={button} chatId={chatId} />,
        });
      }
    });
  }

  if (!isPhoneVisible && !profileLink && !customActionItems.length) {
    return null;
  }

  return (
    <div className={styles['container']} data-testid="UserCardActionsContainer">
      {isPhoneVisible && <UserCardDesktopPhone phone={formatPhone} onClick={handleClickCallButton} />}
      {profileLink && (
        <UserCardDesktopProfileLink
          userProfileUrl={profileLink}
          agentAccountType={agentAccountType}
          onClick={handleClickProfile}
        />
      )}
      {customActionItems.length > 0 && <UserCardActions actions={customActionItems} />}
    </div>
  );
};
